import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useGetMySessions } from "@workspace/api-client-react";
import { apiFetch } from "@/lib/api";
import {
  format, startOfWeek, endOfWeek, addWeeks, subWeeks,
  eachDayOfInterval, isSameDay, parseISO, differenceInMinutes,
  isToday, addDays,
} from "date-fns";
import {
  Calendar, Clock, MapPin, Star, Video, X, Edit3,
  AlertTriangle, CheckCircle, Ban, Loader2,
  List, ChevronLeft, ChevronRight, ClipboardList, Trash2,
} from "lucide-react";
import { SessionNotePanel } from "@/components/session-note-panel";
import { useState, useRef, useMemo } from "react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useGetMySlotWaitlist } from "@workspace/api-client-react";

/* ─── Types ──────────────────────────────────────────────────────────────── */
interface CancelPreview {
  zone: "FULL_REFUND" | "PARTIAL_CREDIT" | "NO_REFUND" | "CREDIT_FULL_REFUND" | "CREDIT_NO_REFUND";
  refundAmountCents: number;
  restoreCredit: boolean;
  paymentType: string;
  priceTotalCents: number;
  noRefundWindowHrs: number;
  partialCreditWindowHrs: number;
  platformCreditPct: number;
  cancellationWindowHours: number;
  isLateCancellation: boolean;
}

/* ─── Calendar constants ─────────────────────────────────────────────────── */
const HOUR_START = 6;
const HOUR_END = 22;
const TOTAL_HOURS = HOUR_END - HOUR_START;
const HOUR_HEIGHT = 64;

const STATUS_BLOCK: Record<string, string> = {
  CONFIRMED:         "bg-green-500/20 border-green-500/40 text-green-300",
  PENDING:           "bg-amber-500/20 border-amber-500/40 text-amber-300",
  AMENDMENT_PENDING: "bg-orange-500/20 border-orange-500/40 text-orange-300",
  COMPLETED:         "bg-blue-500/20 border-blue-500/40 text-blue-300",
  CANCELLED:         "bg-red-500/20 border-red-500/40 text-red-300",
};

const STATUS_LIST: Record<string, string> = {
  PENDING:           "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  CONFIRMED:         "bg-green-500/10 text-green-400 border-green-500/20",
  COMPLETED:         "bg-blue-500/10 text-blue-400 border-blue-500/20",
  CANCELLED:         "bg-red-500/10 text-red-400 border-red-500/20",
  REJECTED:          "bg-red-500/10 text-red-400 border-red-500/20",
  AMENDMENT_PENDING: "bg-orange-500/10 text-orange-400 border-orange-500/20",
  NO_SHOW:           "bg-orange-500/10 text-orange-400 border-orange-500/30",
};

/* ─── Main component ─────────────────────────────────────────────────────── */
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Sessions() {
  const [view, setView] = useState<"calendar" | "list" | "waitlist">("calendar");
  const [tab,  setTab]  = useState<"upcoming" | "past" | "cancelled">("upcoming");
  const { data: sessions, isLoading, refetch } = useGetMySessions();
  const { data: myWaitlist, refetch: refetchWaitlist } = useGetMySlotWaitlist({ query: { enabled: true } });
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  /* rating state */
  const [ratingSession, setRatingSession] = useState<string | null>(null);
  const [rating, setRating] = useState(5);
  const [ratingComment, setRatingComment] = useState("");

  /* cancel state */
  const [cancelConfirm, setCancelConfirm] = useState<string | null>(null);
  const [cancelPreview, setCancelPreview] = useState<CancelPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const cancelRequestRef = useRef<string | null>(null);

  /* calendar week nav */
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));

  const now = new Date();

  const weekDays = useMemo(
    () => eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) }),
    [weekStart]
  );

  const weekSessions = useMemo(
    () => (sessions || []).filter((s: any) => {
      if (!["PENDING", "CONFIRMED", "AMENDMENT_PENDING"].includes(s.status)) return false;
      const d = parseISO(s.proposedStart);
      return d >= weekStart && d <= endOfWeek(weekStart, { weekStartsOn: 1 });
    }),
    [sessions, weekStart]
  );

  const filtered = useMemo(() => (sessions || []).filter((s: any) => {
    if (tab === "upcoming")  return ["PENDING", "CONFIRMED", "AMENDMENT_PENDING"].includes(s.status) && new Date(s.proposedStart) > now;
    if (tab === "past")      return s.status !== "CANCELLED" && s.status !== "REJECTED" && new Date(s.proposedStart) <= now;
    return s.status === "CANCELLED" || s.status === "REJECTED";
  }), [sessions, tab]);

  /* ── actions ── */
  async function openCancelConfirm(id: string) {
    cancelRequestRef.current = id;
    setCancelConfirm(id);
    setCancelPreview(null);
    setPreviewLoading(true);
    try {
      const preview = await apiFetch<CancelPreview>(`/sessions/${id}/cancel-preview`);
      if (cancelRequestRef.current === id) setCancelPreview(preview);
    } catch {
      if (cancelRequestRef.current === id) setCancelPreview(null);
    } finally {
      if (cancelRequestRef.current === id) setPreviewLoading(false);
    }
  }

  function closeCancelConfirm() {
    cancelRequestRef.current = null;
    setCancelConfirm(null);
    setCancelPreview(null);
    setPreviewLoading(false);
  }

  async function cancelSession(id: string) {
    setActionLoading(id);
    try {
      await apiFetch(`/sessions/${id}/cancel`, { method: "PUT" });
      toast({ title: "Session cancelled" });
      closeCancelConfirm();
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally { setActionLoading(null); }
  }

  async function submitRating(sessionId: string) {
    const session = (sessions || []).find((s: any) => s.id === sessionId);
    if (!session) return;
    setActionLoading(sessionId);
    try {
      await apiFetch(`/ratings`, {
        method: "POST",
        body: JSON.stringify({ sessionId, trainerId: session.trainerId, score: rating, comment: ratingComment }),
      });
      toast({ title: "Rating submitted!" });
      setRatingSession(null);
      setRating(5);
      setRatingComment("");
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally { setActionLoading(null); }
  }

  async function requestAmendment(id: string) {
    const session = (sessions || []).find((s: any) => s.id === id);
    if (!session) return;
    const newTime = prompt("Enter new preferred date/time (e.g., 2026-04-01 14:00):");
    if (!newTime) return;
    const newStart = new Date(newTime);
    const originalDuration = new Date(session.proposedEnd).getTime() - new Date(session.proposedStart).getTime();
    const newEnd = new Date(newStart.getTime() + originalDuration);
    setActionLoading(id);
    try {
      await apiFetch(`/sessions/${id}/amend`, {
        method: "PUT",
        body: JSON.stringify({ proposedStart: newStart.toISOString(), proposedEnd: newEnd.toISOString() }),
      });
      toast({ title: "Amendment requested" });
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally { setActionLoading(null); }
  }

  return (
    <AppLayout>
      <div className="flex flex-col gap-6 pb-12">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-1">My Sessions</h1>
            <p className="text-muted-foreground">View and manage your training schedule</p>
          </div>
          <div className="flex items-center gap-1 p-1 rounded-lg bg-white/5 border border-white/10 shrink-0">
            <button
              onClick={() => setView("calendar")}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all",
                view === "calendar" ? "bg-primary text-black" : "text-muted-foreground hover:text-white"
              )}
            >
              <Calendar className="w-4 h-4" /> Calendar
            </button>
            <button
              onClick={() => setView("list")}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all",
                view === "list" ? "bg-primary text-black" : "text-muted-foreground hover:text-white"
              )}
            >
              <List className="w-4 h-4" /> List
            </button>
            <button
              onClick={() => setView("waitlist")}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all relative",
                view === "waitlist" ? "bg-primary text-black" : "text-muted-foreground hover:text-white"
              )}
            >
              <ClipboardList className="w-4 h-4" /> Waitlist
              {(myWaitlist?.length ?? 0) > 0 && (
                <span className={cn("absolute -top-1 -right-1 w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center", view === "waitlist" ? "bg-black text-primary" : "bg-primary text-black")}>
                  {myWaitlist!.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {view === "waitlist" ? (
          <StudentWaitlistPanel
            waitlist={myWaitlist || []}
            onRemove={async (trainerId: string, entryId: string) => {
              try {
                await apiFetch(`/trainers/${trainerId}/waitlist/${entryId}`, { method: "DELETE" });
                toast({ title: "Removed from waitlist" });
                refetchWaitlist();
              } catch {
                toast({ title: "Failed to remove", variant: "destructive" });
              }
            }}
          />
        ) : view === "calendar" ? (
          <WeekCalendar
            weekDays={weekDays}
            weekSessions={weekSessions}
            isLoading={isLoading}
            onPrev={() => setWeekStart(w => subWeeks(w, 1))}
            onNext={() => setWeekStart(w => addWeeks(w, 1))}
            onToday={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}
            onJoinVideo={(id) => navigate(`/video/${id}`)}
          />
        ) : (
          <ListView
            tab={tab}
            setTab={setTab}
            filtered={filtered}
            isLoading={isLoading}
            sessions={sessions || []}
            actionLoading={actionLoading}
            cancelConfirm={cancelConfirm}
            cancelPreview={cancelPreview}
            previewLoading={previewLoading}
            ratingSession={ratingSession}
            rating={rating}
            ratingComment={ratingComment}
            setRating={setRating}
            setRatingComment={setRatingComment}
            setRatingSession={setRatingSession}
            onOpenCancelConfirm={openCancelConfirm}
            onCloseCancelConfirm={closeCancelConfirm}
            onCancelSession={cancelSession}
            onSubmitRating={submitRating}
            onRequestAmendment={requestAmendment}
            onJoinVideo={(id) => navigate(`/video/${id}`)}
          />
        )}
      </div>
    </AppLayout>
  );
}

/* ─── Week Calendar ─────────────────────────────────────────────────────── */
function WeekCalendar({
  weekDays, weekSessions, isLoading, onPrev, onNext, onToday, onJoinVideo,
}: {
  weekDays: Date[];
  weekSessions: any[];
  isLoading: boolean;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onJoinVideo: (id: string) => void;
}) {
  const [selectedSession, setSelectedSession] = useState<any | null>(null);
  const hours = Array.from({ length: TOTAL_HOURS }, (_, i) => HOUR_START + i);
  const now = new Date();
  const nowMins = (now.getHours() - HOUR_START) * 60 + now.getMinutes();
  const nowTop = (nowMins / 60) * HOUR_HEIGHT;

  return (
    <div className="flex flex-col gap-4">
      {/* Nav bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onPrev} className="border-white/10 hover:bg-white/5">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onNext} className="border-white/10 hover:bg-white/5">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={onToday} className="border-white/10 hover:bg-white/5 text-sm">
            Today
          </Button>
        </div>
        <span className="text-white font-semibold text-sm">
          {format(weekDays[0], "MMM d")} – {format(weekDays[6], "MMM d, yyyy")}
        </span>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-400" /> Confirmed</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" /> Pending</span>
          <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-orange-400" /> Reschedule</span>
        </div>
      </div>

      {/* Grid */}
      <div className="rounded-xl border border-white/10 overflow-hidden bg-card">
        {/* Day headers */}
        <div className="grid border-b border-white/10" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
          <div className="border-r border-white/10" />
          {weekDays.map((day) => (
            <div
              key={day.toISOString()}
              className={cn(
                "py-3 text-center border-r border-white/10 last:border-r-0",
                isToday(day) && "bg-primary/5"
              )}
            >
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{format(day, "EEE")}</p>
              <p className={cn("text-lg font-bold mt-0.5", isToday(day) ? "text-primary" : "text-white")}>
                {format(day, "d")}
              </p>
            </div>
          ))}
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto" style={{ maxHeight: "580px" }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-24 text-muted-foreground text-sm">Loading…</div>
          ) : (
            <div
              className="relative grid"
              style={{ gridTemplateColumns: "56px repeat(7, 1fr)", height: `${TOTAL_HOURS * HOUR_HEIGHT}px` }}
            >
              {/* Hour labels */}
              <div className="relative border-r border-white/10">
                {hours.map(h => (
                  <div
                    key={h}
                    className="absolute w-full flex items-start justify-end pr-2"
                    style={{ top: `${(h - HOUR_START) * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
                  >
                    <span className="text-[10px] text-muted-foreground/60 leading-none pt-1">
                      {h === 12 ? "12p" : h > 12 ? `${h - 12}p` : `${h}a`}
                    </span>
                  </div>
                ))}
              </div>

              {/* Day columns */}
              {weekDays.map(day => {
                const daySessions = weekSessions.filter(s => isSameDay(parseISO(s.proposedStart), day));
                const isNow = isToday(day);

                return (
                  <div
                    key={day.toISOString()}
                    className={cn(
                      "relative border-r border-white/10 last:border-r-0",
                      isNow && "bg-primary/[0.02]"
                    )}
                  >
                    {/* Hour lines */}
                    {hours.map(h => (
                      <div
                        key={h}
                        className="absolute w-full border-t border-white/5"
                        style={{ top: `${(h - HOUR_START) * HOUR_HEIGHT}px` }}
                      />
                    ))}

                    {/* Now indicator */}
                    {isNow && nowMins >= 0 && nowMins <= TOTAL_HOURS * 60 && (
                      <div
                        className="absolute left-0 right-0 z-20 flex items-center pointer-events-none"
                        style={{ top: `${nowTop}px` }}
                      >
                        <div className="w-2 h-2 rounded-full bg-primary -ml-1 shrink-0" />
                        <div className="flex-1 border-t border-primary" />
                      </div>
                    )}

                    {/* Session blocks */}
                    {daySessions.map(session => {
                      const start  = parseISO(session.proposedStart);
                      const end    = parseISO(session.proposedEnd);
                      const startMin = (start.getHours() - HOUR_START) * 60 + start.getMinutes();
                      const duration = differenceInMinutes(end, start);
                      const top    = (startMin / 60) * HOUR_HEIGHT;
                      const height = Math.max((duration / 60) * HOUR_HEIGHT, 28);
                      const style  = STATUS_BLOCK[session.status] || "bg-white/10 border-white/20 text-muted-foreground";

                      return (
                        <button
                          key={session.id}
                          onClick={() => setSelectedSession(session)}
                          className={cn(
                            "absolute left-0.5 right-0.5 rounded-md border px-1.5 py-1 text-left z-10 overflow-hidden hover:opacity-80 transition-opacity",
                            style
                          )}
                          style={{ top: `${top}px`, height: `${height}px` }}
                          title={`${session.trainer?.firstName} ${session.trainer?.lastName} · ${format(start, "h:mm a")}–${format(end, "h:mm a")}`}
                        >
                          <p className="text-[11px] font-semibold leading-tight truncate">
                            {session.trainer?.firstName} {session.trainer?.lastName?.[0]}.
                          </p>
                          {height > 40 && (
                            <p className="text-[10px] opacity-70 leading-tight">
                              {format(start, "h:mm")}–{format(end, "h:mm a")}
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Empty state */}
      {!isLoading && weekSessions.length === 0 && (
        <div className="p-10 text-center rounded-2xl border border-dashed border-white/10 bg-white/5">
          <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-white font-medium">No sessions this week</p>
          <p className="text-muted-foreground text-sm mt-1">Navigate to another week or book a new session</p>
        </div>
      )}

      {/* Session detail panel */}
      {selectedSession && (
        <SessionDetailPanel
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
          onJoinVideo={onJoinVideo}
        />
      )}
    </div>
  );
}

/* ─── Session detail panel (calendar popover) ────────────────────────────── */
function SessionDetailPanel({
  session, onClose, onJoinVideo,
}: {
  session: any;
  onClose: () => void;
  onJoinVideo: (id: string) => void;
}) {
  const start = parseISO(session.proposedStart);
  const end   = parseISO(session.proposedEnd);
  const style = STATUS_BLOCK[session.status] || "bg-white/10 border-white/20 text-muted-foreground";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <Card
        className="glass-panel border-white/10 w-full max-w-sm relative z-10 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className={cn("w-2.5 h-2.5 rounded-full shrink-0 mt-0.5", style.includes("green") ? "bg-green-400" : style.includes("amber") ? "bg-amber-400" : style.includes("orange") ? "bg-orange-400" : "bg-blue-400")} />
              <div>
                <CardTitle className="text-white text-base">
                  {session.trainer?.firstName} {session.trainer?.lastName}
                </CardTitle>
                <span className={cn("text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wider", style)}>
                  {session.status.replace("_", " ")}
                </span>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 -mt-1 -mr-1 shrink-0" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 text-sm">
            <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
            <div>
              <p className="text-white font-semibold">{format(start, "EEEE, MMM d, yyyy")}</p>
              <p className="text-muted-foreground text-xs">{format(start, "h:mm a")} — {format(end, "h:mm a")}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-sm">
            {session.mode === "VIDEO" ? (
              <Video className="w-4 h-4 text-muted-foreground shrink-0" />
            ) : (
              <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
            )}
            <p className="text-white">{session.mode === "VIDEO" ? "Video Session" : "In-Person"}</p>
          </div>
          {session.priceTotal && (
            <div className="flex items-center gap-3 text-sm">
              <span className="w-4 h-4 shrink-0 text-center text-muted-foreground font-mono text-xs">$</span>
              <p className="text-primary font-mono font-bold">${session.priceTotal.toFixed(2)}</p>
            </div>
          )}
          {session.mode === "VIDEO" && session.status === "CONFIRMED" && (
            <Button
              className="w-full bg-blue-600 text-white hover:bg-blue-700 mt-2"
              onClick={() => { onJoinVideo(session.id); onClose(); }}
            >
              <Video className="w-4 h-4 mr-2" /> Join Video Session
            </Button>
          )}
          {(session.status === "CONFIRMED" || session.status === "COMPLETED") && (
            <div className="pt-2 border-t border-white/10">
              <SessionNotePanel
                sessionId={session.id}
                otherPartyLabel={session.trainer?.firstName || "trainer"}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── List View ─────────────────────────────────────────────────────────── */
function ListView({
  tab, setTab, filtered, isLoading, sessions, actionLoading,
  cancelConfirm, cancelPreview, previewLoading,
  ratingSession, rating, ratingComment,
  setRating, setRatingComment, setRatingSession,
  onOpenCancelConfirm, onCloseCancelConfirm, onCancelSession,
  onSubmitRating, onRequestAmendment, onJoinVideo,
}: {
  tab: "upcoming" | "past" | "cancelled";
  setTab: (t: "upcoming" | "past" | "cancelled") => void;
  filtered: any[];
  isLoading: boolean;
  sessions: any[];
  actionLoading: string | null;
  cancelConfirm: string | null;
  cancelPreview: CancelPreview | null;
  previewLoading: boolean;
  ratingSession: string | null;
  rating: number;
  ratingComment: string;
  setRating: (n: number) => void;
  setRatingComment: (s: string) => void;
  setRatingSession: (id: string | null) => void;
  onOpenCancelConfirm: (id: string) => void;
  onCloseCancelConfirm: () => void;
  onCancelSession: (id: string) => void;
  onSubmitRating: (id: string) => void;
  onRequestAmendment: (id: string) => void;
  onJoinVideo: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-2 bg-white/5 p-1 rounded-xl w-fit">
        {(["upcoming", "past", "cancelled"] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              "px-5 py-2.5 rounded-lg text-sm font-medium transition-all capitalize",
              tab === t ? "bg-primary text-black font-bold" : "text-muted-foreground hover:text-white"
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />)}</div>
      ) : filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map((session: any) => (
            <Card key={session.id} className="glass-panel border-white/5 p-5">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-xl font-bold text-white shrink-0">
                      {session.trainer?.firstName?.[0] || "T"}
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-lg">
                        {session.trainer?.firstName} {session.trainer?.lastName}
                      </h4>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {format(new Date(session.proposedStart), "MMM d, yyyy")}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {format(new Date(session.proposedStart), "h:mm a")}
                        </span>
                        <span className="flex items-center gap-1">
                          {session.mode === "VIDEO"
                            ? <Video className="w-3.5 h-3.5" />
                            : <MapPin className="w-3.5 h-3.5" />}
                          {session.mode === "VIDEO" ? "Video" : "In Person"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <span className="font-mono text-lg text-primary font-bold">${session.priceTotal?.toFixed(2)}</span>
                      {session.paymentType === "SINGLE" && <p className="text-xs text-muted-foreground">Single session</p>}
                      {session.paymentType === "PACKAGE" && <p className="text-xs text-blue-400">Package credit</p>}
                      {session.paymentType === "MEMBERSHIP" && <p className="text-xs text-purple-400">Membership</p>}
                    </div>
                    {session.status === "NO_SHOW" ? (
                      <span className={cn("flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-semibold border", STATUS_LIST.NO_SHOW)}>
                        <AlertTriangle className="w-3 h-3" /> No Show
                      </span>
                    ) : (
                      <span className={cn("px-3 py-1 rounded-lg text-xs font-medium border", STATUS_LIST[session.status] || "bg-white/5")}>
                        {session.status.replace("_", " ")}
                      </span>
                    )}
                  </div>
                </div>

                {tab === "upcoming" && session.status === "CONFIRMED" && (
                  <div className="border-t border-white/5 pt-2">
                    <SessionNotePanel
                      sessionId={session.id}
                      otherPartyLabel={session.trainer?.firstName || "trainer"}
                    />
                  </div>
                )}

                {tab === "upcoming" && (
                  <div className="flex items-center gap-2 pt-2 border-t border-white/5 flex-wrap">
                    {session.mode === "VIDEO" && session.status === "CONFIRMED" && (
                      <Button size="sm" onClick={() => onJoinVideo(session.id)} className="bg-blue-600 text-white hover:bg-blue-700">
                        <Video className="w-4 h-4 mr-1" /> Join Video
                      </Button>
                    )}
                    {cancelConfirm === session.id ? (
                      <div className="flex flex-col gap-3 w-full">
                        {previewLoading ? (
                          <div className="flex items-center gap-2 py-2">
                            <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />
                            <span className="text-sm text-muted-foreground">Checking cancellation policy…</span>
                          </div>
                        ) : cancelPreview ? (
                          <>
                            {cancelPreview.isLateCancellation && (
                              <div className="flex items-start gap-3 bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                                <AlertTriangle className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-orange-300">Late cancellation — penalty may apply</p>
                                  <p className="text-xs text-orange-400/80 mt-0.5">
                                    This gym requires at least {cancelPreview.cancellationWindowHours}h notice to cancel without penalty. Your trainer will be notified.
                                  </p>
                                </div>
                              </div>
                            )}
                            {cancelPreview.zone === "FULL_REFUND" && (
                              <div className="flex items-start gap-3 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                                <CheckCircle className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-green-300">Full refund</p>
                                  <p className="text-xs text-green-400/80 mt-0.5">You'll receive ${(cancelPreview.refundAmountCents / 100).toFixed(2)} back to your original payment method.</p>
                                </div>
                              </div>
                            )}
                            {cancelPreview.zone === "PARTIAL_CREDIT" && (
                              <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-amber-300">Partial refund only</p>
                                  <p className="text-xs text-amber-400/80 mt-0.5">You're within the cancellation window. You'll receive a {cancelPreview.platformCreditPct}% refund (${(cancelPreview.refundAmountCents / 100).toFixed(2)} of ${(cancelPreview.priceTotalCents / 100).toFixed(2)}).</p>
                                </div>
                              </div>
                            )}
                            {cancelPreview.zone === "NO_REFUND" && (
                              <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                                <Ban className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-red-300">No refund</p>
                                  <p className="text-xs text-red-400/80 mt-0.5">You're within the no-refund window (less than {cancelPreview.noRefundWindowHrs}h before). You will still be charged ${(cancelPreview.priceTotalCents / 100).toFixed(2)}.</p>
                                </div>
                              </div>
                            )}
                            {cancelPreview.zone === "CREDIT_FULL_REFUND" && (
                              <div className="flex items-start gap-3 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                                <CheckCircle className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-green-300">Credit will be restored</p>
                                  <p className="text-xs text-green-400/80 mt-0.5">Your {cancelPreview.paymentType === "PACKAGE" ? "package" : "membership"} credit will be returned.</p>
                                </div>
                              </div>
                            )}
                            {cancelPreview.zone === "CREDIT_NO_REFUND" && (
                              <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                                <Ban className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-red-300">Credit will be forfeited</p>
                                  <p className="text-xs text-red-400/80 mt-0.5">You're within the cancellation window. Your credit will not be restored.</p>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-amber-300">Unable to load cancellation policy</p>
                              <p className="text-xs text-amber-400/80 mt-0.5">Your refund will depend on the gym's cancellation policy.</p>
                            </div>
                          </div>
                        )}
                        <div className="flex items-center gap-2 justify-end">
                          <Button size="sm" variant="ghost" onClick={onCloseCancelConfirm} className="text-muted-foreground">
                            Keep Session
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => onCancelSession(session.id)}
                            disabled={actionLoading === session.id || previewLoading}
                            className={cn(
                              "hover:bg-red-500/10",
                              (cancelPreview?.zone === "NO_REFUND" || cancelPreview?.zone === "CREDIT_NO_REFUND")
                                ? "text-red-400 border border-red-500/30"
                                : "text-red-400"
                            )}
                          >
                            {actionLoading === session.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <X className="w-4 h-4 mr-1" />}
                            {(cancelPreview?.zone === "NO_REFUND" || cancelPreview?.zone === "CREDIT_NO_REFUND") ? "Cancel Anyway" : "Yes, Cancel"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => onOpenCancelConfirm(session.id)} className="text-red-400 hover:bg-red-500/10">
                          <X className="w-4 h-4 mr-1" /> Cancel
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onRequestAmendment(session.id)} className="text-muted-foreground hover:text-white">
                          <Edit3 className="w-4 h-4 mr-1" /> Reschedule
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {tab === "past" && (
                  <div className="space-y-0">
                    <div className="pt-2 border-t border-white/5">
                      {ratingSession === session.id ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map(star => (
                              <button key={star} onClick={() => setRating(star)} className="p-0.5">
                                <Star className={cn("w-6 h-6 transition-colors", star <= rating ? "text-primary fill-primary" : "text-white/20")} />
                              </button>
                            ))}
                          </div>
                          <textarea
                            value={ratingComment}
                            onChange={e => setRatingComment(e.target.value)}
                            placeholder="Leave a comment (optional)…"
                            rows={2}
                            className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-muted-foreground"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => onSubmitRating(session.id)} disabled={actionLoading === session.id} className="bg-primary text-primary-foreground">
                              Submit Rating
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setRatingSession(null)} className="text-muted-foreground">
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => setRatingSession(session.id)} className="text-primary hover:bg-primary/10">
                          <Star className="w-4 h-4 mr-1" /> Rate Session
                        </Button>
                      )}
                    </div>
                    <SessionNotePanel
                      sessionId={session.id}
                      otherPartyLabel={session.trainer?.firstName || "trainer"}
                    />
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="p-12 text-center rounded-2xl border border-dashed border-white/10 bg-white/5">
          <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <p className="text-white text-lg font-medium">No {tab} sessions</p>
        </div>
      )}
    </div>
  );
}

/* ─── Student Waitlist Panel ─────────────────────────────────────────────── */
function StudentWaitlistPanel({ waitlist, onRemove }: {
  waitlist: any[];
  onRemove: (trainerId: string, entryId: string) => void;
}) {
  const [removing, setRemoving] = useState<string | null>(null);

  if (waitlist.length === 0) {
    return (
      <div className="p-12 text-center rounded-2xl border border-dashed border-white/10 bg-white/5">
        <ClipboardList className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
        <p className="text-white text-lg font-medium">No waitlist entries</p>
        <p className="text-muted-foreground text-sm mt-2">When a trainer has no open slots, you can join their waitlist from the booking page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <ClipboardList className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-semibold text-white">My Waitlist</h2>
        <span className="text-sm text-muted-foreground">({waitlist.length} active {waitlist.length === 1 ? "entry" : "entries"})</span>
      </div>
      <p className="text-sm text-muted-foreground">You'll receive a notification as soon as a slot opens up with these trainers.</p>
      {waitlist.map((entry: any) => (
        <div key={entry.id} className="glass-panel border border-white/5 rounded-2xl p-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-secondary border border-white/10 flex items-center justify-center text-lg font-bold text-white shrink-0">
              {entry.trainer?.firstName?.[0] ?? "T"}{entry.trainer?.lastName?.[0] ?? ""}
            </div>
            <div>
              <p className="text-white font-semibold">
                {entry.trainer?.firstName} {entry.trainer?.lastName}
              </p>
              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                {entry.dayOfWeek !== null && entry.dayOfWeek !== undefined && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {DAY_NAMES[entry.dayOfWeek]}s
                  </span>
                )}
                {entry.timeSlot && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {entry.timeSlot}
                  </span>
                )}
                <span>Joined {format(new Date(entry.createdAt), "MMM d, yyyy")}</span>
              </div>
              {entry.notifiedAt && (
                <span className="inline-flex items-center gap-1 mt-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full">
                  ★ Slot opened — book now!
                </span>
              )}
            </div>
          </div>
          <button
            disabled={removing === entry.id}
            onClick={async () => {
              setRemoving(entry.id);
              await onRemove(entry.trainerId, entry.id);
              setRemoving(null);
            }}
            className="p-2 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
          >
            {removing === entry.id
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Trash2 className="w-4 h-4" />
            }
          </button>
        </div>
      ))}
    </div>
  );
}
